import forEach from "lodash/forEach";
import get from "lodash/get";
import isNil from "lodash/isNil";
import isUndefined from "lodash/isUndefined";
import last from "lodash/last";
import set from "lodash/set";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_AVAILABILITY_KEY,
  FIELD_AVAILABILITY_THRESHOLD_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY,
  FIELD_METADATA_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { FormSchemaField } from "PFApp/types/activity_form/activity_form_schema";

export const TABLE_COLUMNS_BLACKLIST = [
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY
];

export const TABULAR_FIELD_DROPDOWN_MIN_WIDTH = 300;

const CHARACTER_WIDTH = 8;
const REQUIRED_INDICATOR_WIDTH = 20;

const getColumnMinWidth = (field: FormSchemaField) => {
  if (field.name === last(FIELD_AVAILABILITY_KEY.split("."))) {
    return 50;
  }

  return 120;
};

export const calculateColumnWidth = (
  field: FormSchemaField,
  title: string,
  required: boolean,
  hasAnyPostUntilChangeReasonSelected?: boolean
) => {
  const minWidth = getColumnMinWidth(field);
  const defaultWidth = Math.max(
    title.length * CHARACTER_WIDTH + (required ? REQUIRED_INDICATOR_WIDTH : 0),
    minWidth
  );

  let customWidth = 0;
  if (field.name === FIELD_DESCRIPTION_KEY) {
    customWidth = 300;
  }
  if (field.name === FIELD_POST_UNTIL_KEY && hasAnyPostUntilChangeReasonSelected) {
    customWidth = 300;
  }

  return Math.max(customWidth, defaultWidth);
};

type FieldEmptyValue = never[] | string | number | null;

export const getClearValue = (field: FormSchemaField): FieldEmptyValue => {
  if (
    ["custom_field", "checkboxes"].includes(field.property?.type ?? "") ||
    (field.property?.type === "select" && field.property?.kind === "multiple") ||
    [FIELD_SKILLS_FRAMEWORK_IDS_KEY, FIELD_ATTACHMENT_IDS_KEY, FIELD_INTEREST_QUESTIONS_KEY].includes(
      field.name
    )
  ) {
    return [];
  }

  if (
    ["text", "url", "link", "string"].includes(field.property?.type ?? "") ||
    [FIELD_NAME_KEY, FIELD_DESCRIPTION_KEY, FIELD_POST_UNTIL_CHANGE_REASON_KEY].includes(field.name)
  ) {
    return "";
  }

  if ([FIELD_AVAILABILITY_THRESHOLD_KEY, FIELD_MIN_REQUIRED_COUNT_KEY].includes(field.name)) {
    return 0;
  }

  if (field.name === FIELD_COMMUNICATION_TYPE_KEY) {
    return "direct";
  }

  return null;
};

export const parseValue = (field: FormSchemaField, rawValue: string): any => {
  let parsedValue: any = rawValue;

  if (
    field.property?.type === "integer" ||
    [
      FIELD_MIN_REQUIRED_COUNT_KEY,
      FIELD_ASSIGNEE_ID_KEY,
      FIELD_BOOKING_CATEGORY_ID_KEY,
      FIELD_AVAILABILITY_THRESHOLD_KEY
    ].includes(field.name)
  ) {
    const numeric = Number(rawValue);
    parsedValue = isNaN(numeric) ? null : numeric;
  }

  if (field.property?.type === "toggle") {
    parsedValue = rawValue === "true";
  }

  if (
    field.property?.type === "date-range" ||
    field.property?.type === "custom_field" ||
    field.property?.type === "checkboxes" ||
    (field.property?.type === "select" && (field.property as any)?.kind === "multiple")
  ) {
    try {
      parsedValue = JSON.parse(rawValue);
    } catch {
      parsedValue = null;
    }
  }

  if (
    [
      FIELD_ATTACHMENT_IDS_KEY,
      FIELD_SKILLS_FRAMEWORK_IDS_KEY,
      FIELD_INTEREST_QUESTIONS_KEY,
      FIELD_PRIVACY_KEY,
      FIELD_PARENT_ACTIVITY_KEY,
      FIELD_POST_UNTIL_KEY,
      last(FIELD_AVAILABILITY_KEY.split("."))
    ].includes(field.name)
  ) {
    try {
      parsedValue = JSON.parse(rawValue);
    } catch {
      parsedValue = null;
    }
  }

  if (field.name === FIELD_COMMUNICATION_TYPE_KEY && !["direct", "group"].includes(rawValue)) {
    parsedValue = null;
  }

  return parsedValue === null ? getClearValue(field) : parsedValue;
};

export const getRoleFormKeyPrefix = (field: FormSchemaField) => {
  if (!field.property && field.name !== "availability") {
    return "";
  }
  if (field.property?.type === "custom_field") {
    return `${FIELD_CUSTOM_FIELDS_KEY}.`;
  }
  return `${FIELD_METADATA_KEY}.`;
};

export const toFieldFormValue = (field: FormSchemaField, value: unknown): unknown =>
  isUndefined(value) ? getClearValue(field) : value;

type FieldValuesContainers = Partial<
  Record<typeof FIELD_CUSTOM_FIELDS_KEY | typeof FIELD_METADATA_KEY, object>
>;

export const withEmptyFieldValues = <T extends FieldValuesContainers>(
  values: T,
  fields: FormSchemaField[]
): T => {
  const result = {
    ...values,
    [FIELD_CUSTOM_FIELDS_KEY]: { ...values[FIELD_CUSTOM_FIELDS_KEY] },
    [FIELD_METADATA_KEY]: { ...values[FIELD_METADATA_KEY] }
  };

  forEach(fields, (field) => {
    const path = `${getRoleFormKeyPrefix(field)}${field.name}`;

    if (isNil(get(result, path))) {
      set(result, path, getClearValue(field));
    }
  });

  return result;
};

const serializeRawValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    return String(value);
  }
  // Arrays, objects (date-range, select-multiple, custom_field, side-panel values)
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

export const serializeValue = (
  field: FormSchemaField,
  fieldPath: string,
  getValues: (path: string) => unknown
): string => {
  if (field.name === FIELD_PRIVACY_KEY) {
    const rowPrefix = fieldPath.slice(0, fieldPath.lastIndexOf(".") + 1);
    return JSON.stringify({
      [FIELD_PRIVACY_KEY]: getValues(fieldPath),
      [FIELD_AUDIENCES_KEY]: getValues(`${rowPrefix}${FIELD_AUDIENCES_KEY}`)
    });
  }

  if (field.name === FIELD_POST_UNTIL_KEY) {
    const rowPrefix = fieldPath.slice(0, fieldPath.lastIndexOf(".") + 1);
    return JSON.stringify({
      [FIELD_POST_UNTIL_KEY]: getValues(fieldPath),
      [FIELD_POST_UNTIL_CHANGE_REASON_KEY]: getValues(`${rowPrefix}${FIELD_POST_UNTIL_CHANGE_REASON_KEY}`)
    });
  }

  if (field.name === FIELD_INTEREST_QUESTIONS_KEY) {
    return JSON.stringify(getValues(fieldPath));
  }

  return serializeRawValue(getValues(fieldPath));
};
